import { useEffect, useMemo, useState } from "react";
import { injectIntl } from "react-intl";
import { Link, withRouter } from "react-router-dom";

import { APP_TITLE, ENVIRONMENT } from "../../Config/Environment";
import { useFetch } from "../Base/Hooks/basicFetch";
import PageDisplay from "../Base/PageDisplay/PageDisplay";
import GridTableDisplay from "../Base/Shared/GridTableDisplay";
import FixtureSearch from "./FixtureSearch";

import "./Fixtures.scss";

const noTimeLimitEnv = ["nonprod"];

const checkTime = (date, hoursDiff) => {
  const nowDate = new Date();
  const compareDate = new Date(date + (date.charAt(-1) !== "Z" ? "Z" : ""));
  const diff = (compareDate.getTime() - nowDate.getTime()) / 3600000;
  return diff < hoursDiff || noTimeLimitEnv.includes(ENVIRONMENT);
};

const getLinkURL = (fixture) => {
  const base = fixture.status === "IN_PROGRESS" ? "game" : "details";
  return {
    pathname: `/${base}/${fixture.fixtureId}`,
    search: location.search,
  };
};

const Fixtures = (props) => {
  const { intl, clearFixtureSpecificDateFromState } = props;
  const { formatMessage } = intl;
  const [searchText, setSearchText] = useState("");
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [queryString, setQueryString] = useState();
  const [authKey, setAuthKey] = useState(null);
  const showSport = ["dev", "sandpit"].includes(ENVIRONMENT);

  let { error, loading, data } = useFetch(
    "/v1/client/fixtures?" + queryString,
    { method: "GET", values: null, key: authKey },
    refreshFlag,
  );

  const columns = [
    formatMessage({
      id: "status",
      defaultMessage: "Status",
    }),
    formatMessage({
      id: "date",
      defaultMessage: "Date",
    }),
    "",
    formatMessage({
      id: "team.home",
      defaultMessage: "Home Team",
    }),
    "",
    formatMessage({
      id: "team.away",
      defaultMessage: "Away Team",
    }),
    formatMessage({
      id: "venue",
      defaultMessage: "Venue",
    }),
    formatMessage({
      id: "season",
      defaultMessage: "Season",
    }),
  ];

  if (showSport) {
    columns.push(
      formatMessage({
        id: "sport",
        defaultMessage: "Sport",
      }),
    );
  }
  // push empty column for selection button
  columns.push("");

  useEffect(() => {
    clearFixtureSpecificDateFromState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const dateFrom = new Date();
    dateFrom.setHours(dateFrom.getHours() - 6);
    const dateTo = new Date();
    dateTo.setHours(dateFrom.getHours() + 144);
    setQueryString("fromUTC=" + dateFrom.toISOString().split(".")[0] + "&toUTC=" + dateTo.toISOString().split(".")[0]);
    setAuthKey(localStorage.getItem("authKey"));
    setRefreshFlag(true);
    // eslint-disable-next-line
  }, []);

  const rows = useMemo(() => {
    if (data) {
      let fixtures = data !== null ? data.data : [];

      fixtures = fixtures.filter((fixture) => !!fixture.venue?.venueId);

      fixtures.forEach((fixture, index) => {
        if (fixture.startTimeLocal) {
          let matchDate = fixture.startTimeLocal.split("T");
          fixtures[index].ISOdate = matchDate[0];
        }
        if (fixture.season) {
          fixtures[index].seasonName = fixture.season.name;
        }
        if (fixture.venue) {
          fixtures[index].venueName = fixture.venue.name;
        }
        if (fixture.competitors) {
          fixture.competitors.forEach((competitor) => {
            if (competitor.isHome) {
              fixtures[index].teamHome = competitor.nameFullLocal;
              fixtures[index].teamHomeLogo = competitor.logo;
            } else {
              fixtures[index].teamAway = competitor.nameFullLocal;
              fixtures[index].teamAwayLogo = competitor.logo;
            }
          });
        }
      });

      if (searchText !== "") {
        fixtures = fixtures.filter((result) => {
          return (
            result.teamHome?.toUpperCase().indexOf(searchText.toUpperCase()) > -1 ||
            result.teamAway?.toUpperCase().indexOf(searchText.toUpperCase()) > -1 ||
            result.venueName?.toUpperCase().indexOf(searchText.toUpperCase()) > -1
          );
        });
      }
      const fixtureList = fixtures
        .sort((a, b) => (a.startTimeLocal < b.startTimeLocal ? -1 : 1))
        .map((fixture) => {
          const fixtureRowData = [
            fixture.status,
            <div className="date-time">
              <span>{fixture.startTimeLocal.split("T")[0]}</span>
              <span style={{ fontWeight: "bold" }}>{fixture.startTimeLocal.split("T")[1].replace(":00", "")}</span>
            </div>,
            <div className="team-logo">
              {fixture.teamHomeLogo && (
                <img className="fixture-logo" src={fixture.teamHomeLogo} alt={fixture.teamHome} />
              )}
            </div>,
            fixture.teamHome,
            <div className="team-logo">
              {fixture.teamAwayLogo && (
                <img className="fixture-logo" src={fixture.teamAwayLogo} alt={fixture.teamAway} />
              )}
            </div>,
            fixture.teamAway,
            fixture.venueName,
            fixture.seasonName,
          ];
          if (showSport) fixtureRowData.push(fixture.sport);
          if (checkTime(fixture.startTimeUTC, 4))
            fixtureRowData.push(
              <Link to={getLinkURL(fixture)} className="link btn btn-sm btn-success">
                <i className="fas fa-angle-right"></i>
              </Link>,
            );
          else
            fixtureRowData.push(
              <div className="btn btn-sm btn-secondary enabled-false">
                <i className="far fa-clock"></i>
              </div>,
            );
          setRefreshFlag(false);
          return fixtureRowData;
        });
      return fixtureList;
    } else {
      return [];
    }
  }, [data, searchText]);

  const pageTitle = formatMessage({ id: "fixtures", defaultMessage: "Fixtures" });
  const documentTitle = `${formatMessage({ id: "fixtures", defaultMessage: "Fixtures" })} - ${APP_TITLE}`;

  return (
    <PageDisplay documentTitle={documentTitle} pageTitle={pageTitle} isLoading={loading} error={error}>
      <FixtureSearch doSearch={setSearchText} />
      <GridTableDisplay
        columns={columns}
        rows={rows}
        headerClass="fixture-list-header"
        rowCellClass="fixture-list-cell"
        tableKeyPrefix="fixture-list"
      />
    </PageDisplay>
  );
};

export default injectIntl(withRouter(Fixtures));
